/**
 * Soft, accessible colour per mood and per prompt depth.
 *
 * Colour is always paired with an emoji + text label, so colour is never the
 * only way information is communicated.
 */

import type { PromptDepth } from "@/lib/journal-prompt-library";

export interface Tint {
  /** Unselected chip / card surface. */
  soft: string;
  /** Selected / filled state. */
  strong: string;
  /** Left-edge or icon accent. */
  accent: string;
}

const MOODS: Record<string, Tint> = {
  anxious: {
    soft: "border-amber-500/25 bg-amber-500/10 text-foreground",
    strong: "border-amber-500/70 bg-amber-500/25 text-foreground",
    accent: "text-amber-600 dark:text-amber-300",
  },
  sad: {
    soft: "border-sky-500/25 bg-sky-500/10 text-foreground",
    strong: "border-sky-500/70 bg-sky-500/25 text-foreground",
    accent: "text-sky-600 dark:text-sky-300",
  },
  frustrated: {
    soft: "border-rose-500/25 bg-rose-500/10 text-foreground",
    strong: "border-rose-500/70 bg-rose-500/25 text-foreground",
    accent: "text-rose-600 dark:text-rose-300",
  },
  exhausted: {
    soft: "border-indigo-500/25 bg-indigo-500/10 text-foreground",
    strong: "border-indigo-500/70 bg-indigo-500/25 text-foreground",
    accent: "text-indigo-600 dark:text-indigo-300",
  },
  numb: {
    soft: "border-slate-500/25 bg-slate-500/10 text-foreground",
    strong: "border-slate-500/70 bg-slate-500/25 text-foreground",
    accent: "text-slate-600 dark:text-slate-300",
  },
  calm: {
    soft: "border-emerald-500/25 bg-emerald-500/10 text-foreground",
    strong: "border-emerald-500/70 bg-emerald-500/25 text-foreground",
    accent: "text-emerald-600 dark:text-emerald-300",
  },
  hopeful: {
    soft: "border-teal-500/25 bg-teal-500/10 text-foreground",
    strong: "border-teal-500/70 bg-teal-500/25 text-foreground",
    accent: "text-teal-600 dark:text-teal-300",
  },
  grateful: {
    soft: "border-gold/30 bg-gold/10 text-foreground",
    strong: "border-gold bg-gold/30 text-foreground",
    accent: "text-[var(--gold-deep)]",
  },
  strong: {
    soft: "border-orange-500/25 bg-orange-500/10 text-foreground",
    strong: "border-orange-500/70 bg-orange-500/25 text-foreground",
    accent: "text-orange-600 dark:text-orange-300",
  },
  thriving: {
    soft: "border-lime-500/25 bg-lime-500/10 text-foreground",
    strong: "border-lime-600/70 bg-lime-500/25 text-foreground",
    accent: "text-lime-700 dark:text-lime-300",
  },
};

const NEUTRAL: Tint = {
  soft: "border-border/60 bg-card text-foreground",
  strong: "border-foreground/40 bg-muted text-foreground",
  accent: "text-muted-foreground",
};

export function moodTint(mood?: string | null): Tint {
  if (!mood) return NEUTRAL;
  return MOODS[mood.toLowerCase()] ?? NEUTRAL;
}

export interface DepthStyle {
  label: string;
  emoji: string;
  card: string;
  chip: string;
  accent: string;
}

export const DEPTH_STYLES: Record<PromptDepth, DepthStyle> = {
  gentle: {
    label: "Gentle",
    emoji: "🌿",
    card: "border-emerald-500/30 bg-gradient-to-br from-emerald-500/12 via-card to-card",
    chip: "border border-emerald-500/40 bg-emerald-500/15 text-emerald-800 dark:text-emerald-200",
    accent: "text-emerald-700 dark:text-emerald-300",
  },
  deeper: {
    label: "Deeper",
    emoji: "🌊",
    card: "border-sky-500/30 bg-gradient-to-br from-sky-500/12 via-card to-card",
    chip: "border border-sky-500/40 bg-sky-500/15 text-sky-800 dark:text-sky-200",
    accent: "text-sky-700 dark:text-sky-300",
  },
  "deep-dive": {
    label: "Deep Dive",
    emoji: "🌙",
    card: "border-purple-500/30 bg-gradient-to-br from-purple-500/14 via-card to-card",
    chip: "border border-purple-500/40 bg-purple-500/15 text-purple-800 dark:text-purple-200",
    accent: "text-purple-700 dark:text-purple-300",
  },
};
